/**
 * Coast cart upgrade: converts one-time cart lines to their subscription selling plan.
 *
 * Lives in a real asset rather than inline in `coast-cart-upgrade.liquid` because the
 * cart section is re-rendered via morph/innerHTML — inline <script> tags injected that
 * way never execute, so the listener has to be delegated from a file loaded once.
 */

const EVENT = 'shopify:cart:lines-update';

/**
 * @typedef {{ plan: number, threeVariant: number | null, threePlan: number | null }} Plan
 */

/**
 * @param {HTMLElement} root
 * @returns {Map<number, Plan>} variant id -> its plan, plus the same-flavour 3-Pack variant/plan for 1-Packs
 */
function parsePlans(root) {
  const plans = new Map();
  for (const chunk of (root.dataset.lines || '').split(',')) {
    const [variant, plan, threeVariant, threePlan] = chunk.split('|');
    if (!variant || !plan) continue;
    plans.set(Number(variant), {
      plan: Number(plan),
      threeVariant: threeVariant ? Number(threeVariant) : null,
      threePlan: threePlan ? Number(threePlan) : null,
    });
  }
  return plans;
}

function routeRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

async function cartJson(path, init) {
  const response = await fetch(`${routeRoot()}${path}`, init);
  if (!response.ok) throw new Error(`${path} failed: ${response.status}`);
  return response.json();
}

function fetchCart() {
  return cartJson('cart.js', { headers: { Accept: 'application/json' } });
}

/**
 * Line keys are not stable across this operation: once a line gets a selling
 * plan Shopify re-keys/merges the BXGY-split lines, so a second change.js call
 * with a key captured earlier fails with "no valid id or line parameter".
 * Address lines by their current 1-based index and re-read the cart each time.
 */
async function upgradeLines(plans) {
  let cart = await fetchCart();
  cart = await foldIntoThreePacks(cart, plans);
  for (let guard = 0; guard < 20; guard += 1) {
    const index = cart.items.findIndex(
      (item) => !item.selling_plan_allocation && plans.has(item.variant_id),
    );
    if (index === -1) return cart;
    const item = cart.items[index];
    cart = await cartJson('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: index + 1, quantity: item.quantity, selling_plan: plans.get(item.variant_id).plan }),
    });
  }
  return cart;
}

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

/**
 * One-time 1-Pack pouches (incl. Buy 2 Get 1 mix & match) are replaced by
 * 3-Pack subscriptions, one per 3 pouches, so the customer gets the 3-Pack
 * subscription price the product page promises (15% off) rather than three
 * full-price 1-Pack subscriptions. Mixed flavours: each set takes the flavour
 * with the most pouches left. Leftover pouches (<3) stay 1-Pack and get their
 * 4-week plan in upgradeLines().
 */
async function foldIntoThreePacks(cart, plans) {
  const remaining = new Map();
  for (const item of cart.items) {
    const plan = plans.get(item.variant_id);
    if (item.selling_plan_allocation || !plan || !plan.threeVariant) continue;
    remaining.set(item.variant_id, (remaining.get(item.variant_id) || 0) + item.quantity);
  }

  let total = 0;
  for (const qty of remaining.values()) total += qty;
  const sets = Math.floor(total / 3);
  if (sets === 0) return cart;

  const threePacks = new Map();
  for (let set = 0; set < sets; set += 1) {
    const byQty = [...remaining.entries()].sort((a, b) => b[1] - a[1]);
    const [variantId] = byQty[0];
    threePacks.set(variantId, (threePacks.get(variantId) || 0) + 1);
    let need = 3;
    for (const [id, qty] of byQty) {
      if (need === 0) break;
      const take = Math.min(qty, need);
      remaining.set(id, qty - take);
      need -= take;
    }
  }

  const additions = [];
  for (const [variantId, count] of threePacks) {
    const plan = plans.get(variantId);
    additions.push({ id: plan.threeVariant, quantity: count, selling_plan: plan.threePlan });
  }
  for (const [variantId, qty] of remaining) {
    if (qty > 0) additions.push({ id: variantId, quantity: qty, selling_plan: plans.get(variantId).plan });
  }

  // Add the replacements first so a failure never empties the bag, then drop
  // the one-time 1-Pack lines by current index, re-reading each time (BXGY
  // lines re-key after every mutation). The added lines carry a selling plan,
  // so the removal loop skips them.
  await cartJson('cart/add.js', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items: additions }),
  });
  cart = await fetchCart();
  for (let guard = 0; guard < 20; guard += 1) {
    const index = cart.items.findIndex(
      (item) => !item.selling_plan_allocation && remaining.has(item.variant_id),
    );
    if (index === -1) break;
    cart = await cartJson('cart/change.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ line: index + 1, quantity: 0 }),
    });
  }
  return cart;
}

/**
 * Keeps the product page purchase toggle in sync — it reads its state from
 * sessionStorage (see coast-purchase-toggle.liquid), so flip both the stored
 * value and any mounted radio.
 */
function syncPurchaseToggle() {
  try {
    sessionStorage.setItem('coast-purchase-mode', 'subscribe');
  } catch (err) {
    /* private mode — the radio dispatch below still covers the current page */
  }

  document
    .querySelectorAll('coast-subscribe-option [data-coast-row="subscribe"] [data-coast-mode]')
    .forEach((input) => {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

const CONFETTI_COLORS = ['#e8425f', '#ff8fa3', '#ffd166', '#2ec4b6', '#ffffff'];

/**
 * Self-contained confetti burst — a fixed full-screen canvas that removes itself
 * once every piece has fallen. No external library (theme CSP + no bundler here).
 *
 * @param {HTMLElement} origin element the burst radiates from
 */
function confetti(origin) {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483647';
  document.body.appendChild(canvas);

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const box = origin.getBoundingClientRect();
  const originX = box.left + box.width / 2;
  const originY = box.top + box.height / 2;

  const pieces = Array.from({ length: 90 }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
    const speed = 7 + Math.random() * 9;
    return {
      x: originX + (Math.random() - 0.5) * box.width * 0.6,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + Math.random() * 6,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.3,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    };
  });

  let frame = 0;

  function tick() {
    frame += 1;
    ctx.clearRect(0, 0, width, height);

    let alive = false;
    for (const piece of pieces) {
      piece.vy += 0.32; // gravity
      piece.vx *= 0.99;
      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.rotation += piece.spin;
      if (piece.y - piece.size > height) continue;

      alive = true;
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - frame / 150);
      ctx.translate(piece.x, piece.y);
      ctx.rotate(piece.rotation);
      ctx.fillStyle = piece.color;
      ctx.fillRect(-piece.size / 2, -piece.size / 2, piece.size, piece.size * 0.6);
      ctx.restore();
    }

    if (alive && frame < 150) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  }

  requestAnimationFrame(tick);
}

/**
 * Tells the theme the cart changed. When `sections` (from a `sections=` cart
 * request) is passed, cart-items-component morphs that HTML in place instead
 * of fetching the section again — one round trip fewer.
 */
function notify(cart, sections) {
  const event = new Event(EVENT, { bubbles: true, cancelable: true });
  event.promise = Promise.resolve({
    cart: null,
    detail: { itemCount: cart?.item_count, items: cart?.items, sections },
  });
  document.dispatchEvent(event);
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

/** @param {HTMLElement} root */
async function upgrade(root) {
  const button = root.querySelector('[data-coast-upgrade-button]');
  const label = root.querySelector('[data-coast-upgrade-label]');
  const error = root.querySelector('[data-coast-upgrade-error]');
  const plans = parsePlans(root);
  if (!plans.size || root.hasAttribute('data-busy')) return;

  root.setAttribute('data-busy', '1');
  button.disabled = true;
  error.hidden = true;
  label.textContent = 'Switching…';

  try {
    const cart = await upgradeLines(plans);
    syncPurchaseToggle();
    confetti(button);
    notify(cart);
  } catch (err) {
    console.warn('[coast-cart-upgrade]', err);
    root.removeAttribute('data-busy');
    button.disabled = false;
    label.textContent = 'Yes, give me 15% off + free shipping';
    error.hidden = false;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-coast-upgrade-button]');
  if (!button) return;

  const root = button.closest('coast-cart-upgrade');
  if (root) upgrade(root);
});

/*
 * Buy 2 Get 1 Free stepper. cart-products.liquid collapses Shopify's paid/free
 * split lines into one paid row (stepper shows paid pouches) + one FREE row.
 * The theme's own handler would change a single split line; instead we drive
 * the variant's one-time pouch total and let Shopify re-split it:
 *   paid P  ->  total T = P + floor(P / 2)   (every 2 paid earns 1 free)
 *
 * Fast path: one POST /cart/update.js keyed by the line keys the row was
 * rendered with, asking for the cart sections in the same response, so a tap
 * costs a single round trip. Taps within 150ms coalesce (same as the theme).
 * If the keys went stale (another tab, a failed morph) we fall back to the
 * read-then-mutate path.
 */
const BXGY_EVENT = 'quantity-selector:update';
const BXGY_DEBOUNCE_MS = 150;
let bxgyQueue = Promise.resolve();
/** @type {Map<number, { paid: number, timer: number }>} */
const bxgyPending = new Map();

function pouchTotal(paid) {
  return paid + Math.floor(paid / 2);
}

function freeCount(total) {
  return Math.floor(total / 3);
}

/** @param {HTMLElement} row */
function poolLines(row) {
  return (row.dataset.coastLines || '')
    .split(',')
    .map((chunk) => chunk.split('='))
    .filter(([key, qty]) => key && qty)
    .map(([key, qty]) => ({ key, quantity: Number(qty) }));
}

/** Spread `target` pouches over the rendered lines: grow the first, shrink from the end. */
function planUpdates(lines, target) {
  const updates = {};
  let remaining = target;
  for (const line of lines) {
    const quantity = Math.min(line.quantity, remaining);
    updates[line.key] = quantity;
    remaining -= quantity;
  }
  if (remaining > 0 && lines.length) updates[lines[0].key] += remaining;
  return updates;
}

/** Optimistic feedback while the request is in flight. */
function previewBxgy(variantId, total) {
  const free = document.querySelector(`[data-coast-bxgy-free="${variantId}"]`);
  const label = free && free.querySelector('[data-coast-free-label]');
  if (label) label.textContent = `${freeCount(total)} free`;
  if (free) free.hidden = freeCount(total) === 0;

  const row = document.querySelector(`[data-coast-bxgy="${variantId}"]`);
  row && row.querySelector('.cart-items__price text-component')?.shimmer?.();
  document.querySelector('cart-items-component [ref="cartTotal"]')?.shimmer?.();
}

async function updatePool(variantId, target) {
  const row = document.querySelector(`[data-coast-bxgy="${variantId}"]`);
  const lines = row instanceof HTMLElement ? poolLines(row) : [];
  if (!lines.length) return setOneTimeTotal(variantId, target).then((cart) => ({ cart, sections: null }));

  try {
    const cart = await cartJson('cart/update.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        updates: planUpdates(lines, target),
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    return { cart, sections: cart.sections };
  } catch (err) {
    console.warn('[coast-cart-bxgy] fast path failed, falling back', err);
    const cart = await setOneTimeTotal(variantId, target);
    return { cart, sections: null };
  }
}

/** Slow path: read the cart, then add or trim until the pool matches. */
async function setOneTimeTotal(variantId, target) {
  let cart = await fetchCart();
  const isPool = (item) => !item.selling_plan_allocation && item.variant_id === variantId;
  const current = cart.items.filter(isPool).reduce((sum, item) => sum + item.quantity, 0);

  if (target > current) {
    await cartJson('cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: variantId, quantity: target - current }] }),
    });
    return fetchCart();
  }

  let remove = current - target;
  for (let guard = 0; guard < 20 && remove > 0; guard += 1) {
    const index = cart.items.findIndex(isPool);
    if (index === -1) break;
    const item = cart.items[index];
    const take = Math.min(item.quantity, remove);
    cart = await cartJson('cart/change.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ line: index + 1, quantity: item.quantity - take }),
    });
    remove -= take;
  }
  return cart;
}

function queueBxgy(variantId, target) {
  previewBxgy(variantId, target);
  bxgyQueue = bxgyQueue
    .then(() => updatePool(variantId, target))
    .catch((err) => {
      console.warn('[coast-cart-bxgy]', err);
      return fetchCart().then((cart) => ({ cart, sections: null }));
    })
    .then(({ cart, sections }) => notify(cart, sections));
}

document.addEventListener(
  BXGY_EVENT,
  (event) => {
    const row = event.target instanceof Element && event.target.closest('[data-coast-bxgy]');
    if (!row) return;
    event.stopImmediatePropagation();
    const paid = Number(event.detail && event.detail.quantity);
    if (!Number.isFinite(paid) || paid < 0) return;

    const variantId = Number(row.dataset.coastBxgy);
    const pending = bxgyPending.get(variantId);
    if (pending) window.clearTimeout(pending.timer);
    const timer = window.setTimeout(() => {
      bxgyPending.delete(variantId);
      queueBxgy(variantId, pouchTotal(paid));
    }, BXGY_DEBOUNCE_MS);
    bxgyPending.set(variantId, { paid, timer });
  },
  true,
);

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-coast-bxgy-remove]');
  if (!button) return;
  // The trash lives on the paid row, or on the FREE row when nothing is paid.
  const free = button.closest('[data-coast-bxgy-free]');
  const row =
    button.closest('[data-coast-bxgy]') ||
    (free instanceof HTMLElement && document.querySelector(`[data-coast-bxgy="${free.dataset.coastBxgyFree}"]`));
  if (!(row instanceof HTMLElement)) return;
  const variantId = Number(row.dataset.coastBxgy);
  const pending = bxgyPending.get(variantId);
  if (pending) window.clearTimeout(pending.timer);
  bxgyPending.delete(variantId);
  row.style.opacity = '0.4';
  queueBxgy(variantId, 0);
});
